import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import DrawerMenu from './components/DrawerMenu/DrawerMenu';
import Header from './components/Header/Header';
import SimpleHeader from './components/Header/SimpleHeader';
import AuthHeader from './components/Header/AuthHeader';
import ProductHeader from './product/components/ProductHeader/ProductHeader';
import {TermsPage, PrivacyPage, SubscriptionTermsPage, SupportPage} from './pages/Legal/LegalPages';
import Quiz from './pages/Quiz';
import PlanSelection from './pages/PlanSelection';
import SubscriptionSuccess from './funnel/Checkout/SubscriptionSuccess';
import SubscriptionError from './funnel/Checkout/SubscriptionError';
import './App.css';
import './i18n/config';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from './store/useAuthStore';
import { getCurrentUser } from './api/productService';
import Checkout from './funnel/Checkout/Checkout';
import ToastProvider from "./components/Toast/ToastProvider";
import {ToastContainer} from "./components/Toast";
import {listOfLang} from "./i18n/config";
import {pushInfoToGoogleData} from "./utils/googleTag";
import { buildAttributionPayload } from './utils/attribution';
import { quizService } from './api/quizService';
import {facebookEventFire} from "./utils/facebookEventFire";
import PayPalSuccess from "./funnel/Checkout/PayPalSuccess";
import {isEmptyObject} from "./utils/funnelHelpers";
const LAST_VISITED_STEP_KEY = 'last_visited_step';

const AppContent: React.FC = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isLanguageInitialized, setIsLanguageInitialized] = useState(false);
    const location = useLocation();
    const { i18n, t } = useTranslation();
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    // Show back button on all pages except the first quiz step
    const showBackButton = !(location.pathname === '/quiz/f1/1/v1' || location.pathname === '/');

    // Check if current page should use ProductHeader
    const useProductHeader = ['/courses', '/boost', '/dashboard'].includes(location.pathname);

    // Check if current page should use SimpleHeader
    const useSimpleHeader = ['/subscription', '/delete-account', '/change-email', '/feedback', '/language', '/recommended-courses'].includes(location.pathname);

    // Check if current page should use AuthHeader (for non-authenticated pages and PIN setup)
    const useAuthHeader = (!isAuthenticated && ['/login'].includes(location.pathname)) || ['/pin', '/confirm-pin'].includes(location.pathname);

    // Check if we should show back button in AuthHeader (for PIN step only, not PIN pages)
    const showAuthHeaderBackButton = useAuthHeader && location.search.includes('step=pin') && !['/pin', '/confirm-pin'].includes(location.pathname);

    // Check if current page should use CourseDetailHeader (for course detail pages)
    const useCourseDetailHeader = location.pathname.match(/^\/course\/\d+$/);

    // Check if current page is a learning page (has its own header)
    const isLearningPage = location.pathname.match(/^\/course\/\d+\/lesson\/\d+\/learn$/);

    // Get the title for SimpleHeader based on the current path
    const getSimpleHeaderTitle = () => {
        switch (location.pathname) {
            case '/subscription':
                return t('product.subscription.title');
            case '/delete-account':
                return t('product.deleteAccount.title');
            case '/change-email':
                return t('product.changeEmail.title');
            case '/feedback':
                return t('product.feedback.title');
            case '/language':
                return t('product.language.title');
            case '/recommended-courses':
                return t('product.courses.recommendedForYou');
            default:
                return '';
        }
    };

    // Get the right button configuration for SimpleHeader
    const getSimpleHeaderRightButton = () => {
        if (location.pathname === '/recommended-courses') {
            return {
                icon: '/images/ui/filter.png',
                onClick: () => {
                    // TODO: Implement filter functionality
                    console.log('Filter button clicked');
                },
                ariaLabel: 'Filter courses'
            };
        }
        return undefined;
    };

    // Pages that should not show any header or bottom navigation
    const hideHeaderAndNav = ['/subscription/success', '/subscription/failed', '/confirm-email-change'].includes(location.pathname);

    // Pages that should hide only bottom navigation (but keep header)
    // const hideBottomNav = ['/pin', '/confirm-pin'].includes(location.pathname);

    // Initialize language and step on component mount
    useEffect(() => {
        // console.log("DATA LAYER IDENTITY", window.dataLayer)
        const initializeApp = async () => {
            if (location.pathname === '/' || location.pathname === '/quiz/f1/1/v1') {
                pushInfoToGoogleData({
                    event: 'quiz_open'
                });
                facebookEventFire('QuizOpen');
            }
            try {
                let userLang;

                if (isAuthenticated) {
                    try {
                        // Try to get language from user profile
                        const userProfile = await getCurrentUser();
                        if (userProfile?.user.lang) {
                            userLang = userProfile.user.lang;
                        }
                    } catch (error) {
                        console.error('Error fetching user profile:', error);
                    }
                }

                // Fallback to browser language or stored preference if not authenticated
                // or if profile fetch failed
                if (!userLang) {
                    userLang = localStorage.getItem('user_language') ||
                              navigator.language.split('-')[0];
                }
                if(!listOfLang.includes(userLang)){
                    userLang = 'en';
                }

                // Map 'us' to 'en' since we only support 'en' locale
                if (userLang === 'us') {
                    userLang = 'en';
                }
                if(userLang === 'uk'){
                    userLang = 'en'
                }

                // Store the language preference
                localStorage.setItem('user_language', userLang);

                // Change language without causing re-render issues
                await i18n.changeLanguage(userLang);

                // Get initial step
                const lastVisitedStep = localStorage.getItem(LAST_VISITED_STEP_KEY);
                if (lastVisitedStep) {
                    const parsedStep = parseInt(lastVisitedStep, 10);
                    if (!isNaN(parsedStep) && parsedStep > 0) {
                        // step = parsedStep;
                    }
                }

                setIsLanguageInitialized(true);
            } catch (error) {
                console.error('Error initializing app:', error);
                setIsLanguageInitialized(true);
            }
        };

        initializeApp();
    }, [i18n, isAuthenticated, location.pathname]);

    // Capture attribution on every route change (page view)
    useEffect(() => {
        try {
            const payload = buildAttributionPayload();
            if (!isEmptyObject(payload?.click_ids)) {
              localStorage.setItem('click_ids', JSON.stringify(payload.click_ids));
            }
            const FIRST_TOUCH_KEY = 'attribution_first_touch';
            let first_touch: any | undefined = undefined;
            if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
                first_touch = {
                    captured_at: payload.captured_at,
                    landing_page: payload.landing_page,
                    referrer: payload.referrer,
                    utm: payload.utm,
                    click_ids: payload.click_ids,
                };
                localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(first_touch));
            }

            quizService.saveAttributionTouch({
                user_id: payload.user_id,
                created_at: payload.captured_at,
                acq_model: 'last_touch',
                first_touch,
                last_touch: {
                    captured_at: payload.captured_at,
                    landing_page: payload.landing_page,
                    referrer: payload.referrer,
                    utm: payload.utm,
                    click_ids: payload.click_ids,
                    device: payload.device_info.device,
                    os: payload.device_info.os,
                    browser: payload.device_info.browser,
                    country: payload.geo.country,
                    language: payload.geo.language,
                    timezone: payload.geo.timezone,
                },
            });
        } catch (e) {
            console.warn('Attribution capture failed', e);
        }
    }, [location.pathname, location.search]);

    // Don't render routes until language is initialized to prevent redirect loop
    if (!isLanguageInitialized) {
        return (
            <div className="app">
                <div></div>
            </div>
        );
    }

    return (
        <div className="app">
            {!hideHeaderAndNav && location.pathname !== '/profile' && !useCourseDetailHeader && !isLearningPage && (
                useProductHeader ? (
                    <ProductHeader streakCount={9} />
                ) : useSimpleHeader ? (
                    <SimpleHeader
                        title={getSimpleHeaderTitle()}
                        rightButton={getSimpleHeaderRightButton()}
                    />
                ) : useAuthHeader ? (
                    <AuthHeader showBackButton={showAuthHeaderBackButton} />
                ) : (
                    <Header onMenuClick={toggleDrawer} showBackButton={showBackButton} />
                )
            )}
            <main className={isAuthenticated ? 'app__content_product' : 'app__content'}>
                <DrawerMenu isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
                <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/quiz/:funnelId/:stepIndex/:version" element={<Quiz />} />
                    <Route path="/plans" element={<PlanSelection />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/subscription-terms" element={<SubscriptionTermsPage />} />
                    <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                    <Route path="/subscription/paypal-success" element={<PayPalSuccess />} />
                    <Route path="/subscription/failed" element={<SubscriptionError />} />
                </Routes>
            </main>
            {/*{isAuthenticated && !hideHeaderAndNav && !hideBottomNav && <BottomNav />}*/}
        </div>
    );
};

const RootRedirect: React.FC = () => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    if (isAuthenticated) {
        return <Navigate to="//quiz/f1/1/v1" replace />;
    }
    return <Navigate to="/quiz/f1/1/v1" replace />;
};

const App: React.FC = () => {
    return (<ToastProvider>
                <Router>
                    <AppContent />
                </Router>
                <ToastContainer />
            </ToastProvider>
    );
};

export default App;
